import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, Output, ViewChild } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

import { imageMark, IMAGE_FALLBACK_ALT, parseRichText } from '../../data/utils/rich-text.util'

//* Contador de MÓDULO, no de instancia: el alta monta cuatro editores y los tres campos comparten
//* una sola cola de subida. Con un contador por instancia, dos editores emitirían `adjunto:1` y la
//* segunda imagen pisaría a la primera al resolver los tokens.
let tokenSeq = 0

/** Elementos que el navegador crea al dar Enter y que valen por un salto de línea al serializar. */
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI'])

/**
 * Editor de los textos del Tablero: prosa con imágenes pegadas que se ven **como imágenes**
 * mientras se escribe, no como el `![captura.png](https://…)` que quedaba a la vista antes.
 *
 * **El valor sigue siendo el mismo markdown que ya guarda el backend.** Este componente solo cambia
 * cómo se ve al capturarlo: `writeValue` lo parsea a nodos y cada edición lo serializa de vuelta.
 * Nada que exista en la base necesita migrarse y el contrato con la API no se mueve.
 *
 * **Por qué un `contenteditable` a mano y no un editor de librería.** El formato guardado tiene una
 * sola marca (la imagen) y el requisito es verla; traer Quill o TipTap para eso significa cargar un
 * editor de HTML enterito y, con él, la posibilidad de guardar HTML escrito por usuarios —
 * exactamente la superficie de XSS persistido que `app-worklog-rich-text` documenta haber evitado.
 * Aquí el DOM editable solo contiene nodos de texto, `<br>` y figuras creadas por este componente:
 * el pegado de texto se fuerza a plano, así que no hay forma de meter HTML ajeno.
 */
@Component({
  selector: 'app-worklog-editor',
  templateUrl: './worklog-editor.component.html',
  styleUrls: ['./worklog-editor.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => WorklogEditorComponent), multi: true }],
})
export class WorklogEditorComponent implements ControlValueAccessor, OnDestroy {
  @Input() placeholder = ''
  /** Alto mínimo en renglones, para que sustituya a un `<textarea rows="N">` sin cambiar el layout. */
  @Input() minRows = 3

  /** Imagen pegada: el host decide cuándo y cómo se sube; aquí ya quedó visible con su token. */
  @Output() imagePasted = new EventEmitter<{ token: string; file: File }>()
  /** El usuario quitó una imagen: el host la saca de la cola de subida. */
  @Output() imageRemoved = new EventEmitter<string>()

  @ViewChild('box', { static: true }) box!: ElementRef<HTMLDivElement>

  empty = true
  disabled = false

  //* Última cadena que este editor emitió. `writeValue` la compara para NO reconstruir el DOM con
  //* el valor que él mismo acaba de producir: reconstruirlo manda el caret al inicio en cada tecla.
  private lastValue = ''
  //* Token → object URL local de la imagen recién pegada. Es lo que da vista previa inmediata,
  //* antes de que exista adjunto en el servidor.
  private previews: Record<string, string> = {}
  //* Referencias de las imágenes que había en el editable la última vez que se serializó. Sirve
  //* para saber cuáles desaparecieron: la ✕ avisa sola, pero un Backspace, un Cmd+Z o un
  //* seleccionar-todo-y-borrar quitan la imagen sin pasar por ningún handler nuestro, y el host
  //* se quedaba creyendo que seguía ahí.
  private tracked = new Set<string>()

  private onChange: (value: string) => void = () => undefined
  private onTouched: () => void = () => undefined

  // ─────────── ControlValueAccessor ───────────

  writeValue(value: string | null): void {
    const next = value ?? ''
    if (next === this.lastValue) return
    this.lastValue = next
    this.render(next)
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled
  }

  ngOnDestroy(): void {
    for (const url of Object.values(this.previews)) URL.revokeObjectURL(url)
    this.previews = {}
  }

  // ─────────── API para el host ───────────

  /** Cambia el token de una imagen por su URL definitiva, ya subida. No toca el caret. */
  resolveToken(token: string, url: string): void {
    const fig = this.figureFor(token)
    if (!fig) return
    fig.dataset['src'] = url
    //* Se renombra en el baseline ANTES de serializar: si no, el cambio de token a URL definitiva
    //* se leería como «desapareció el token» y se avisaría de un borrado que no ocurrió.
    this.tracked.delete(token)
    this.tracked.add(url)
    this.emit()
  }

  /** Quita una imagen por su token (p. ej. si su subida falló). */
  removeImage(token: string): void {
    const fig = this.figureFor(token)
    if (!fig) return
    this.dropFigure(fig)
    this.emit()
  }

  focus(): void {
    this.box?.nativeElement.focus()
  }

  // ─────────── Eventos del editable ───────────

  onInput(): void {
    this.emit()
  }

  onBlur(): void {
    this.onTouched()
  }

  /**
   * Enter inserta un `<br>` en vez de dejar que el navegador cree `<div>`s anidados. El DOM se
   * mantiene plano (texto, `<br>` y figuras), que es lo único que el serializador tiene que
   * entender — y lo que garantiza que no entre estructura HTML por la puerta de atrás.
   */
  onKeydown(event: KeyboardEvent): void {
    //* `isComposing`: con un IME (acentos de teclado alternativo, escrituras asiáticas) el Enter
    //* confirma el candidato en curso, no crea un renglón. Interceptarlo ahí rompe la escritura.
    if (event.key !== 'Enter' || event.isComposing) return
    event.preventDefault()
    this.insertLineBreak()
    this.emit()
  }

  /**
   * Pegar. Dos caminos y ninguno deja pasar HTML:
   * - imágenes → se quedan visibles en el punto del cursor y se avisa al host para que las suba;
   * - cualquier otra cosa → se inserta como texto plano, así que copiar de Word o de otra web no
   *   mete su marcado en un campo que después se guarda tal cual.
   */
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.items ?? [])
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f)

    if (files.length) {
      event.preventDefault()
      for (const file of files) this.insertImage(file)
      this.emit()
      return
    }

    const text = event.clipboardData?.getData('text/plain')
    if (text == null) return
    event.preventDefault()
    this.insertText(text)
    this.emit()
  }

  /**
   * Soltar dentro del editor. Simétrico al pegado, y por el mismo motivo: si se deja pasar el drop
   * nativo, arrastrar texto con formato desde otra página mete SU marcado en el editable. El
   * serializador lo aplanaría a texto al guardar, así que no llega a la base, pero mientras tanto
   * se ve un formato que después desaparece — y rompe la promesa de que aquí solo hay texto,
   * `<br>` y figuras.
   */
  onDrop(event: DragEvent): void {
    const files = Array.from(event.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'))
    event.preventDefault()
    if (files.length) {
      for (const file of files) this.insertImage(file)
      this.emit()
      return
    }
    const text = event.dataTransfer?.getData('text/plain')
    if (!text) return
    this.insertText(text)
    this.emit()
  }

  /**
   * La ✕ de una figura. Va por delegación en el contenedor porque las figuras se crean a mano
   * (fuera de la plantilla) y no pueden llevar un `(click)` de Angular.
   */
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null
    const remove = target?.closest('.wl-ed-rm') as HTMLElement | null
    if (!remove) return
    event.preventDefault()
    const fig = remove.closest('.wl-ed-fig') as HTMLElement | null
    //* No se avisa aquí: lo hace `emit()` al ver que la referencia ya no está. Un solo camino para
    //* las dos formas de quitarla —la ✕ y el teclado— es lo que evita que una avise y la otra no.
    if (fig) this.dropFigure(fig)
    this.emit()
  }

  // ─────────── Construcción del DOM (sin innerHTML) ───────────

  /** Reconstruye el editable a partir del markdown. Solo se llama con un valor que no es el propio. */
  private render(md: string): void {
    const host = this.box?.nativeElement
    if (!host) return
    //* Las vistas previas se revocan aquí y no solo en `ngOnDestroy`: el diálogo de alta no se
    //* destruye entre altas —se reusa—, así que sin esto cada tarea registrada dejaba sus blobs en
    //* memoria hasta recargar la pestaña. `render` solo corre cuando el valor llega de fuera (un
    //* reset o una carga), nunca mientras se escribe, así que no puede quitar una preview en uso.
    for (const url of Object.values(this.previews)) URL.revokeObjectURL(url)
    this.previews = {}
    //* El baseline se rehace con lo que se va a pintar: si conservara lo anterior, el primer
    //* `emit()` después de un reset avisaría de borrados que en realidad fueron un cambio de valor.
    this.tracked = new Set()
    host.textContent = ''
    for (const seg of parseRichText(md)) {
      if (seg.kind === 'text') for (const node of this.textNodes(seg.value)) host.appendChild(node)
      else {
        host.appendChild(this.figure(seg.value, seg.alt))
        this.tracked.add(seg.value)
      }
    }
    this.syncEmpty(md)
  }

  /** Texto con sus saltos de línea como `<br>`, para que el editable los muestre. */
  private textNodes(text: string): Node[] {
    const out: Node[] = []
    const lines = text.split('\n')
    lines.forEach((line, i) => {
      if (i > 0) out.push(document.createElement('br'))
      if (line) out.push(document.createTextNode(line))
    })
    return out
  }

  /**
   * Una imagen dentro del texto. Es `contenteditable="false"` para que se comporte como un bloque
   * atómico: se selecciona entera y Backspace la borra de una, en vez de dejar al usuario editando
   * "dentro" de la imagen.
   */
  private figure(ref: string, alt?: string): HTMLElement {
    const fig = document.createElement('span')
    fig.className = 'wl-ed-fig'
    fig.contentEditable = 'false'
    fig.dataset['src'] = ref
    fig.dataset['alt'] = alt || IMAGE_FALLBACK_ALT

    const src = this.srcFor(ref)
    if (src) {
      const img = document.createElement('img')
      img.src = src
      img.alt = alt || IMAGE_FALLBACK_ALT
      fig.appendChild(img)
    } else {
      //* Sin `src` utilizable no se pone un `<img>` roto: se pone una caja con el nombre, que dice
      //* qué hay ahí y se puede borrar igual. Hoy solo se llega aquí si alguien enlaza al editor un
      //* texto que YA traía imágenes: la marca se conserva intacta al serializar, así que se ve la
      //* caja en vez de la imagen pero no se pierde nada. Para verlas haría falta pasarle el mapa
      //* de object URLs, y eso se agrega el día que exista edición del intake — no antes.
      const ph = document.createElement('span')
      ph.className = 'wl-ed-ph'
      ph.textContent = alt || IMAGE_FALLBACK_ALT
      fig.appendChild(ph)
    }

    const rm = document.createElement('button')
    rm.type = 'button'
    rm.className = 'wl-ed-rm'
    rm.title = 'Quitar imagen'
    rm.textContent = '✕'
    fig.appendChild(rm)
    return fig
  }

  /**
   * Saca una figura dejando un salto en su lugar. El salto importa: la imagen ocupaba su propio
   * renglón por CSS, no por un `<br>`, así que quitarla a secas pegaba el texto de antes con el de
   * después en una sola línea («…la imagen.Texto después…»).
   */
  private dropFigure(fig: HTMLElement): void {
    fig.replaceWith(document.createElement('br'))
  }

  /** Vista previa local de una imagen recién pegada. */
  private srcFor(ref: string): string | null {
    return this.previews[ref] ?? null
  }

  private figureFor(ref: string): HTMLElement | null {
    return Array.from(this.box.nativeElement.querySelectorAll<HTMLElement>('.wl-ed-fig')).find((f) => f.dataset['src'] === ref) ?? null
  }

  // ─────────── Inserción en el cursor ───────────

  private insertImage(file: File): void {
    const token = `adjunto:${++tokenSeq}`
    this.previews[token] = URL.createObjectURL(file)
    const fig = this.figure(token, file.name)
    //* La imagen se inserta en el cursor y no al final: quien pega lo hace donde está describiendo,
    //* y mandarla al final rompe la relación entre el texto y lo que ilustra.
    this.insertNode(fig)
    this.insertLineBreak()
    this.imagePasted.emit({ token, file })
  }

  private insertText(text: string): void {
    const fragment = document.createDocumentFragment()
    for (const node of this.textNodes(text)) fragment.appendChild(node)
    this.insertNode(fragment)
  }

  private insertLineBreak(): void {
    const br = document.createElement('br')
    this.insertNode(br)
    //* Un `<br>` final no se pinta: el navegador necesita un segundo para mostrar el renglón nuevo.
    //* El serializador ignora el último `<br>` del editable, así que este relleno no entra al valor.
    if (!br.nextSibling) br.parentNode?.appendChild(document.createElement('br'))
  }

  /** Inserta en la selección actual, o al final si el foco no está dentro del editable. */
  private insertNode(node: Node): void {
    const host = this.box.nativeElement
    const selection = window.getSelection()
    let range: Range

    if (selection?.rangeCount && host.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      range = selection.getRangeAt(0)
      range.deleteContents()
    } else {
      range = document.createRange()
      range.selectNodeContents(host)
      range.collapse(false)
    }

    //* Se guarda el último nodo ANTES de insertar: un DocumentFragment se vacía al insertarse y
    //* después ya no se le puede preguntar dónde termina para dejar ahí el caret.
    const last = node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild : node
    range.insertNode(node)
    if (last) {
      range.setStartAfter(last)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
  }

  // ─────────── Serialización de vuelta a markdown ───────────

  private emit(): void {
    const value = this.serialize(this.box.nativeElement, true)
    this.lastValue = value
    this.syncEmpty(value)

    const presentes = new Set(
      Array.from(this.box.nativeElement.querySelectorAll<HTMLElement>('.wl-ed-fig'))
        .map((f) => f.dataset['src'])
        .filter((ref): ref is string => !!ref)
    )
    const idas = [...this.tracked].filter((ref) => !presentes.has(ref))
    this.tracked = presentes

    this.onChange(value)
    for (const ref of idas) this.imageRemoved.emit(ref)
  }

  /**
   * Recorre el editable y devuelve el markdown. Es recursivo porque, aunque el editor mantiene el
   * DOM plano, el navegador puede envolver texto en un `<div>` al arrastrar y soltar dentro del
   * propio campo.
   */
  private serialize(node: Node, root = false): string {
    let out = ''
    const children = Array.from(node.childNodes)

    children.forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        //* El editable escribe `&nbsp;` para los espacios seguidos; guardarlo dejaría espacios que
        //* no se pueden buscar ni cortar.
        out += (child.nodeValue ?? '').replace(/\u00a0/g, ' ')
        return
      }
      if (!(child instanceof HTMLElement)) return

      const ref = child.dataset?.['src']
      //* Una figura se emite entera y NO se recorre por dentro: si se recorriera, el texto del
      //* botón de quitar («✕») acabaría dentro de la descripción guardada.
      if (ref) {
        out += imageMark(child.dataset['alt'], ref)
        return
      }
      if (child.tagName === 'BR') {
        //* El último `<br>` del editable es el relleno que el navegador necesita para mostrar un
        //* renglón vacío; contarlo agregaría un salto fantasma al final en cada guardado.
        if (root && index === children.length - 1) return
        out += '\n'
        return
      }
      if (BLOCK_TAGS.has(child.tagName) && out && !out.endsWith('\n')) out += '\n'
      out += this.serialize(child)
    })

    return out
  }

  private syncEmpty(value: string): void {
    this.empty = !value.trim()
  }
}
